import { useCallback, useEffect, useRef } from "react";
import { trackPiece } from "../trackerBus";

const DWELL_INTERVAL_MS = 30_000;

/**
 * Tempo na sala + movimentos — alimenta modelo preditivo de lance.
 * eventos: auction_room_enter | auction_dwell | auction_room_leave | auction_room_visibility
 */
export function useAuctionRoomTracker(auction) {
  const auctionId = auction?.id;
  const enterRef = useRef(null);
  const metaRef = useRef({ title: "", currentPrice: null });
  metaRef.current = {
    title: auction?.title || "",
    currentPrice: auction?.current_price ?? null,
  };

  const secondsInRoom = useCallback(() => {
    if (!enterRef.current) return 0;
    return Math.round((Date.now() - enterRef.current) / 1000);
  }, []);

  const trackMovement = useCallback(
    (eventType, extra = {}) => {
      if (!auctionId) return;
      const dwell = extra.duration_seconds ?? secondsInRoom();
      trackPiece(eventType, {
        auctionId,
        title: metaRef.current.title,
        price: extra.price ?? metaRef.current.currentPrice,
        currentPrice: metaRef.current.currentPrice,
        duration_seconds: dwell,
        ...extra,
      });
    },
    [auctionId, secondsInRoom],
  );

  useEffect(() => {
    if (!auctionId) return undefined;

    enterRef.current = Date.now();
    trackMovement("auction_room_enter");

    const dwellId = setInterval(() => {
      if (document.hidden) return;
      trackMovement("auction_dwell");
    }, DWELL_INTERVAL_MS);

    const onVisibility = () => {
      trackMovement("auction_room_visibility", {
        visibility: document.visibilityState,
      });
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearInterval(dwellId);
      document.removeEventListener("visibilitychange", onVisibility);
      trackMovement("auction_room_leave");
      enterRef.current = null;
    };
  }, [auctionId, trackMovement]);

  return { trackMovement, secondsInRoom };
}
